import React, { useState, useRef, useEffect, useCallback } from 'react';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { CXButton, CXTextInput } from '../../components/CXForm';
import SimOFPTable from '../../components/Sim/SimOFPTable';
import { pushToast } from '../../slice/appSlice';
import { selectSim, setSimFlightPlanTemplates } from '../../slice/simSlice';
import { getSimPlanTemplates, postSimTemplate } from '../../services/simAPI';
import { selectAuth } from '../../slice/authSlice';
import Access from '../../components/Access';
import { AccessType } from '../../constants/roleAccessConstant';
import { useLocation } from 'react-router-dom';

import TablePagination from '../../components/CXForm/TablePagination';

enum ResponseType {
  OK = 'ok',
  REIMPORT = 'reimport',
}

type SortBy = 'std' | 'ufi' | 'aircraftType' | 'aircraftReg';
type SortOrder = 'asc' | 'desc';

const STORAGE_LIMIT_KEY = 'simTemplateConfiguration.limit';
const STORAGE_LIST_STATE_KEY = 'simTemplateConfiguration.listState';
const STORAGE_COMMITTED_KEYWORD_KEY = 'simTemplateConfiguration.committedKeyword';
const STORAGE_RESTORE_ON_NEXT_MOUNT_KEY = 'simTemplateConfiguration.restoreOnNextMount';
const STORAGE_COMMITTED_TAG_KEY = 'simTemplateConfiguration.committedTag';

const readPersistedLimit = (fallback: number) => {
  try {
    const raw = localStorage.getItem(STORAGE_LIMIT_KEY);
    const n = raw ? Number(raw) : NaN;
    return Number.isFinite(n) && n > 0 ? n : fallback;
  } catch {
    return fallback;
  }
};

const writePersistedLimit = (value: number) => {
  try {
    localStorage.setItem(STORAGE_LIMIT_KEY, String(value));
  } catch {
    // ignore
  }
};

const normalizePaged = (res: any, fallbackPage: number, fallbackLimit: number) => {
  const data = res?.data ?? res ?? {};
  const records = data?.records ?? data?.data?.records ?? data?.items ?? [];
  const page = Number(data?.page ?? data?.data?.page ?? fallbackPage);
  const limit = Number(data?.limit ?? data?.data?.limit ?? fallbackLimit);
  const totalPages = Number(data?.totalPages ?? data?.data?.totalPages ?? 1);

  return {
    records: Array.isArray(records) ? records : [],
    page: Number.isFinite(page) && page > 0 ? page : fallbackPage,
    limit: Number.isFinite(limit) && limit > 0 ? limit : fallbackLimit,
    totalPages: Number.isFinite(totalPages) && totalPages > 0 ? totalPages : 1,
  };
};

type PersistedListState = {
  page: number;
  serverPage?: number;
  limit: number;
  totalPages?: number;
  serverTotalPages?: number;
  searchString: string;
  committedKeyword: string;

  searchTag: string;
  committedTag: string;

  records?: any[];

  sortBy?: SortBy;
  sortOrder?: SortOrder;
};

const readPersistedListState = (): PersistedListState | null => {
  try {
    const raw = sessionStorage.getItem(STORAGE_LIST_STATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed) return null;

    const page = Number(parsed.page);
    const serverPage = parsed.serverPage !== undefined ? Number(parsed.serverPage) : undefined;
    const limit = Number(parsed.limit);
    const totalPages = parsed.totalPages !== undefined ? Number(parsed.totalPages) : undefined;
    const serverTotalPages =
      parsed.serverTotalPages !== undefined ? Number(parsed.serverTotalPages) : undefined;
    const searchString = String(parsed.searchString ?? '');
    const committedKeyword = String(parsed.committedKeyword ?? '');

    const searchTag = String(parsed.searchTag ?? '');
    const committedTag = String(parsed.committedTag ?? '');
    const records = Array.isArray(parsed.records) ? parsed.records : undefined;

    const sortBy = parsed.sortBy as SortBy | undefined;
    const sortOrder = parsed.sortOrder as SortOrder | undefined;

    if (!Number.isFinite(page) || page < 1) return null;
    if (!Number.isFinite(limit) || limit < 1) return null;
    if (serverPage !== undefined && (!Number.isFinite(serverPage) || serverPage < 1)) return null;
    if (totalPages !== undefined && (!Number.isFinite(totalPages) || totalPages < 1)) return null;
    if (serverTotalPages !== undefined && (!Number.isFinite(serverTotalPages) || serverTotalPages < 1)) return null;

    return {
      page,
      serverPage,
      limit,
      totalPages,
      serverTotalPages,
      searchString,
      committedKeyword,
      searchTag,
      committedTag,
      records,
      sortBy,
      sortOrder,
    };
  } catch {
    return null;
  }
};

const writePersistedListState = (state: PersistedListState) => {
  try {
    sessionStorage.setItem(STORAGE_LIST_STATE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
};

const SimTemplateConfigurationPage = () => {
  const { pathname: routePath } = useLocation();
  const dispatch = useAppDispatch();
  const [searchString, setSearchString] = useState('');
  const [searchTag, setSearchTag] = useState('');

  const { simFlightPlanTemplates } = useAppSelector(selectSim);
  const { userName } = useAppSelector(selectAuth);
  const importNewTemplateRef = useRef<HTMLInputElement>(null);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => readPersistedLimit(25));
  const [totalPages, setTotalPages] = useState(1);
  const [serverTotalPages, setServerTotalPages] = useState(1);

  const [sortBy, setSortBy] = useState<SortBy | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<SortOrder | undefined>(undefined);

  // Single source of truth for committed search keyword
  const committedKeywordRef = useRef<string>('');
  const committedTagRef = useRef<string>('');

  const prevSearchStringRef = useRef<string>('');
  const prevSearchTagRef = useRef<string>('');

  const didInitRef = useRef(false);
  const skipNextClearEffectRef = useRef(false);

  // Helper: get committed keyword (with sessionStorage fallback)
  const getCommittedKeyword = useCallback(() => {
    const inRef = committedKeywordRef.current?.trim() || '';
    if (inRef) return inRef;

    try {
      const raw = sessionStorage.getItem(STORAGE_COMMITTED_KEYWORD_KEY);
      return (raw || '').trim();
    } catch {
      return '';
    }
  }, []);

  const getCommittedTag = useCallback(() => {
    const inRef = committedTagRef.current?.trim() || '';
    if (inRef) return inRef;

    try {
      const raw = sessionStorage.getItem(STORAGE_COMMITTED_TAG_KEY);
      return (raw || '').trim();
    } catch {
      return '';
    }
  }, []);

  // Helper: set committed keyword (updates ref + sessionStorage)
  const setCommittedKeyword = useCallback((keyword: string) => {
    committedKeywordRef.current = keyword;

    try {
      if (keyword) {
        sessionStorage.setItem(STORAGE_COMMITTED_KEYWORD_KEY, keyword);
      } else {
        sessionStorage.removeItem(STORAGE_COMMITTED_KEYWORD_KEY);
      }
    } catch {
      // ignore
    }
  }, []);

  const setCommittedTag = useCallback((tag: string) => {
    committedTagRef.current = tag;

    try {
      if (tag) {
        sessionStorage.setItem(STORAGE_COMMITTED_TAG_KEY, tag);
      } else {
        sessionStorage.removeItem(STORAGE_COMMITTED_TAG_KEY);
      }
    } catch {
      // ignore
    }
  }, []);

  const isSearchMode = useCallback(() => {
    return !!getCommittedKeyword() || !!getCommittedTag();
  }, [getCommittedKeyword, getCommittedTag]);

  const getTemplates = useCallback(
    (
      reqPage: number,
      reqLimit: number,
      nextSortBy?: SortBy,
      nextSortOrder?: SortOrder,
      searchKeyword?: string,
      tag?: string,
    ) => {
      getSimPlanTemplates({
        page: reqPage,
        limit: reqLimit,
        ...(searchKeyword ? { ufi: searchKeyword } : {}),
        ...(tag ? { tag } : {}),
        ...(nextSortBy ? { sortBy: nextSortBy } : {}),
        ...(nextSortOrder ? { sortOrder: nextSortOrder } : {}),
      })
        .then((res: any) => {
          const normalized = normalizePaged(res, reqPage, reqLimit);

          if (
            normalized.records.length === 0 &&
            normalized.totalPages > 0 &&
            reqPage > normalized.totalPages
          ) {
            getTemplates(
              normalized.totalPages,
              reqLimit,
              nextSortBy,
              nextSortOrder,
              searchKeyword,
              tag
            );
            return;
          }

          setPage(normalized.page);
          setLimit(reqLimit);
          writePersistedLimit(reqLimit);

          setTotalPages(normalized.totalPages);
          setServerTotalPages(normalized.totalPages);

          dispatch(setSimFlightPlanTemplates(normalized.records));

          writePersistedListState({
            page: normalized.page,
            serverPage: normalized.page,
            limit: reqLimit,
            totalPages: normalized.totalPages,
            serverTotalPages: normalized.totalPages,
            searchString,
            committedKeyword: searchKeyword || '',
            searchTag,
            committedTag: tag || '',
            records: normalized.records,
            sortBy: nextSortBy,
            sortOrder: nextSortOrder,
          });
        })
        .catch((err: any) => {
          console.log(err);
        });
    },
    [dispatch, searchString, searchTag]
  );

  const refreshKeepSort = useCallback(() => {
    const persisted = readPersistedLimit(25);
    const effectiveLimit = Number.isFinite(limit) && limit > 0 ? limit : persisted;
    const safePage = Math.min(Math.max(1, page), Math.max(1, serverTotalPages));
    const keyword = getCommittedKeyword();
    const tag = getCommittedTag();
    getTemplates(safePage, effectiveLimit, sortBy, sortOrder, keyword || undefined, tag || undefined);
  }, [getTemplates, page, serverTotalPages, sortBy, sortOrder, limit, getCommittedKeyword, getCommittedTag]);

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;

    const persistedLimit = readPersistedLimit(25);
    const allowRestore = sessionStorage.getItem(STORAGE_RESTORE_ON_NEXT_MOUNT_KEY) === '1';

    if (!allowRestore) {
      sessionStorage.removeItem(STORAGE_LIST_STATE_KEY);
      setSearchString('');
      setSearchTag('');
      setCommittedKeyword('');
      setCommittedTag('');
      getTemplates(1, persistedLimit);
      return;
    }

    sessionStorage.removeItem(STORAGE_RESTORE_ON_NEXT_MOUNT_KEY);

    const restored = readPersistedListState();

    if (restored) {
      skipNextClearEffectRef.current = true;

      setSearchString(restored.searchString);
      setSearchTag(restored.searchTag);
      setPage(restored.page);
      setLimit(restored.limit);
      setTotalPages(restored.totalPages || 1);
      setServerTotalPages(restored.serverTotalPages || restored.totalPages || 1);
      setSortBy(restored.sortBy);
      setSortOrder(restored.sortOrder);

      const keyword = restored.committedKeyword.trim();
      const tag = restored.committedTag.trim();
      setCommittedKeyword(keyword);
      setCommittedTag(tag);

      if (Array.isArray(restored.records)) {
        writePersistedLimit(restored.limit);
        dispatch(setSimFlightPlanTemplates(restored.records));
        return;
      }

      const serverPage = restored.serverPage ?? restored.page;
      getTemplates(
        serverPage,
        restored.limit || persistedLimit,
        restored.sortBy,
        restored.sortOrder,
        keyword || undefined,
        tag || undefined
      );
      return;
    }

    setLimit(persistedLimit);
    setCommittedKeyword('');
    setCommittedTag('');
    getTemplates(1, persistedLimit);
  }, [dispatch, getTemplates, setCommittedKeyword, setCommittedTag]);

  useEffect(() => {
    if (skipNextClearEffectRef.current) {
      skipNextClearEffectRef.current = false;
      prevSearchStringRef.current = searchString;
      prevSearchTagRef.current = searchTag;
      return;
    }

    const prevS = prevSearchStringRef.current.trim();
    const currS = searchString.trim();
    const prevT = prevSearchTagRef.current.trim();
    const currT = searchTag.trim();

    prevSearchStringRef.current = searchString;
    prevSearchTagRef.current = searchTag;

    const justClearedKeyword = prevS !== '' && currS === '';
    const justClearedTag = prevT !== '' && currT === '';

    const bothEmptyNow = currS === '' && currT === '';

    if (!(bothEmptyNow && (justClearedKeyword || justClearedTag))) return;

    setCommittedKeyword('');
    setCommittedTag('');

    const persisted = readPersistedLimit(25);
    const effectiveLimit = Number.isFinite(limit) && limit > 0 ? limit : persisted;

    getTemplates(1, effectiveLimit, sortBy, sortOrder);
  }, [searchString, searchTag, getTemplates, sortBy, sortOrder, limit, setCommittedKeyword, setCommittedTag]);

  // Effect: persist state on unmount
  useEffect(() => {
    return () => {
      writePersistedListState({
        page,
        serverPage: page,
        limit,
        totalPages,
        serverTotalPages,
        searchString,
        committedKeyword: committedKeywordRef.current,
        searchTag,
        committedTag: committedTagRef.current,
        records: simFlightPlanTemplates || [],
        sortBy,
        sortOrder,
      });
    };
  }, [page, limit, totalPages, serverTotalPages, searchString, searchTag, sortBy, sortOrder, simFlightPlanTemplates]);

  const triggerFileExplorer = () => {
    if (importNewTemplateRef?.current) {
      importNewTemplateRef.current.click();
    }
  };

  const selectFile = (e: any) => {
    if (!!e.target.files?.[0] && userName) {
      const bodyFormData = new FormData();
      bodyFormData.append('eff-file', e.target.files?.[0]);
      bodyFormData.append('created-by', userName);

      postSimTemplate(bodyFormData)
        .then((res: any) => {
          if (res.status === 500) {
            throw res;
          }
          if (res.status === ResponseType.REIMPORT) {
            dispatch(pushToast({ type: 'success', message: 'The SIM template re-imported' }));
          } else if (res.status === ResponseType.OK) {
            dispatch(pushToast({ type: 'success', message: 'New SIM template added' }));
          }

          // Clear sort
          setSortBy(undefined);
          setSortOrder(undefined);
          setPage(1);

          // Clear search input and committed keyword
          setCommittedKeyword('');
          setCommittedTag('');
          setSearchString('');
          setSearchTag('');

          const effectiveLimit =
            Number.isFinite(limit) && limit > 0
              ? limit
              : readPersistedLimit(25);

          getTemplates(1, effectiveLimit);
        })
        .catch((err: any) => {
          if (err.status === 500) {
            dispatch(pushToast({ type: 'error', message: 'Import SIM template failed' }));
          } else {
            console.log(err);
          }
        });
    }

    e.target.value = '';
  };

  const search = useCallback(() => {
    const keyword = searchString.trim().toUpperCase();
    const tag = searchTag.trim();

    const persisted = readPersistedLimit(25);
    const effectiveLimit = Number.isFinite(limit) && limit > 0 ? limit : persisted;

    setPage(1);

    if (!keyword && !tag) {
      setCommittedKeyword('');
      setCommittedTag('');
      getTemplates(1, effectiveLimit, sortBy, sortOrder);
      return;
    }

    setCommittedKeyword(keyword);
    setCommittedTag(tag);
    getTemplates(1, effectiveLimit, sortBy, sortOrder, keyword || undefined, tag || undefined);
  }, [searchString, searchTag, sortBy, sortOrder, getTemplates, limit, setCommittedKeyword, setCommittedTag]);

  return (
    <>
      <div className={'CathaySansBd'} style={{ fontSize: '2rem', lineHeight: '2.25rem' }}>
        Sim Template Configuration
      </div>

      <div className={'section d-flex justify-content-between'}>
        <div className={'p-2 ps-0 d-flex'}>

          <div style={{ width: '360px', marginRight: '16px' }}>
            <CXTextInput value={searchString} placeholder={'Search by template number'} onChange={setSearchString} />
          </div>
          <div style={{ width: '360px', marginRight: '16px' }}>
            <CXTextInput value={searchTag} placeholder={'Search by tag'} onChange={setSearchTag} />
          </div>

          <CXButton outline={false} text={'Search'} onClick={() => search()} />
        </div>

        <div className={'p-2'}>
          <Access route={routePath} access={[AccessType.CREATE]}>
            <CXButton outline={true} text={'Import New Template'} onClick={() => triggerFileExplorer()} />
            <input ref={importNewTemplateRef} onChange={selectFile} type="file" accept={'.eff'} hidden multiple />
          </Access>
        </div>
      </div>

      <div className={'section'}>
        <div className="limitTopRight" style={{ marginBottom: 12 }}>
          <TablePagination
            limit={limit}
            totalPages={totalPages}
            currentPage={page}
            hidePages={true}
            hideLimit={false}
            onChangeLimit={(value: any) => {
              const nextLimit = Number(value);
              if (!Number.isFinite(nextLimit) || nextLimit <= 0) return;

              setLimit(nextLimit);
              writePersistedLimit(nextLimit);

              const keyword = getCommittedKeyword();
              const tag = getCommittedTag();
              getTemplates(1, nextLimit, sortBy, sortOrder, keyword || undefined, tag || undefined);
            }}
            onChangePage={() => { }}
          />
        </div>

        <SimOFPTable
          simOFPs={simFlightPlanTemplates || []}
          isSimTemplateConfiguration={true}
          routePath={routePath}
          sortMode={'server'}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onMutateDone={refreshKeepSort}
          onSort={(key) => {
            if (key === 'status') return;
            const nextSortBy: SortBy = key;
            const nextSortOrder: SortOrder = sortBy !== key ? 'asc' : sortOrder === 'asc' ? 'desc' : 'asc';

            setSortBy(nextSortBy);
            setSortOrder(nextSortOrder);

            const persisted = readPersistedLimit(25);
            const effectiveLimit = Number.isFinite(limit) && limit > 0 ? limit : persisted;
            const keyword = getCommittedKeyword();
            const tag = getCommittedTag();

            getTemplates(1, effectiveLimit, nextSortBy, nextSortOrder, keyword || undefined, tag || undefined);
          }}
        />

        <TablePagination
          limit={limit}
          totalPages={totalPages}
          currentPage={page}
          hideLimit={true}
          hidePages={false}
          onChangeLimit={() => { }}
          onChangePage={(value: any) => {
            const next = Number(value);
            if (!Number.isFinite(next) || next < 1) return;
            if (next > serverTotalPages) return;

            const persisted = readPersistedLimit(25);
            const effectiveLimit = Number.isFinite(limit) && limit > 0 ? limit : persisted;

            const keyword = getCommittedKeyword();
            const tag = getCommittedTag();

            getTemplates(next, effectiveLimit, sortBy, sortOrder, keyword || undefined, tag || undefined);
          }}
        />
      </div>
    </>
  );
};

export default SimTemplateConfigurationPage;
